import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Activity,
  Clock,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  ExternalLink,
  ChevronRight,
  BarChart3,
  RotateCcw,
  Radio,
  ArrowUpFromLine,
  ArrowDownToLine,
  Pause,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { ROUTES } from '@/lib/routes';
import { useDashboardData } from '@/hooks/useDashboard';
import { useDlqStats } from '@/hooks/useDlq';
import { getCurrentQuota } from '@/api/companies-api';
import { resetCircuitBreaker } from '@/api/dashboard-api';
import { getPollingStatus, type PollingStatusResponse } from '@/api/polling-status-api';
import { listEntityPauses } from '@/api/entity-pause-api';
import { toast } from '@/store/toast-store';
import type { QuotaSummary } from '@/api/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { TimeSeriesChart, EntityMetricsTable } from '@/components/dashboard';
import { cn } from '@/lib/cn';
import type { ConnectionStatus, QueueHealth, EntityStatus } from '@/types/dashboard';

// ── Helper Components ──

function StatusIndicator({ status }: { status: 'healthy' | 'degraded' | 'critical' | 'connected' | 'disconnected' | 'down' | 'not-configured' }) {
  const config = {
    healthy: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-400' },
    connected: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-400' },
    degraded: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-400' },
    critical: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400' },
    disconnected: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400' },
    down: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400' },
    'not-configured': { icon: AlertTriangle, color: 'text-gray-400', bg: 'bg-gray-400' },
  };

  const { icon: Icon, color, bg } = config[status] ?? config.disconnected;

  return (
    <div className="flex items-center gap-2">
      <span className={cn('h-2 w-2 rounded-full', bg, status === 'healthy' || status === 'connected' ? 'animate-pulse' : '')} />
      <Icon className={cn('h-4 w-4', color)} />
    </div>
  );
}

function ConnectionCard({ label, connection, resetTarget, onCircuitReset }: {
  label: string;
  connection: ConnectionStatus;
  /** The adapter type to send to the reset endpoint (epicor, cat2, etc.) */
  resetTarget?: string;
  onCircuitReset?: () => void;
}) {
  const [isResetting, setIsResetting] = useState(false);

  const statusTextMap: Record<string, string> = {
    connected: 'Connected',
    degraded: 'Degraded',
    disconnected: 'Disconnected',
    'not-configured': 'Not Configured',
  };
  const statusText = statusTextMap[connection.status] ?? 'Disconnected';

  const statusColorMap: Record<string, string> = {
    connected: 'text-emerald-400',
    degraded: 'text-amber-400',
    disconnected: 'text-red-400',
    'not-configured': 'text-gray-400',
  };
  const statusColor = statusColorMap[connection.status] ?? 'text-red-400';

  const isCircuitOpen = connection.circuitState === 'OPEN' || connection.circuitState === 'HALF_OPEN';
  const circuitText = connection.circuitState === 'OPEN' ? 'Circuit Open' : connection.circuitState === 'HALF_OPEN' ? 'Recovering' : '';

  const handleResetCircuit = useCallback(async () => {
    if (!resetTarget) return;
    setIsResetting(true);
    try {
      await resetCircuitBreaker(resetTarget);
      toast(`Circuit breaker for "${label}" reset to CLOSED`, { variant: 'success' });
      onCircuitReset?.();
    } catch (err) {
      toast(`Failed to reset circuit breaker: ${err instanceof Error ? err.message : 'Unknown error'}`, { variant: 'error' });
    } finally {
      setIsResetting(false);
    }
  }, [resetTarget, label, onCircuitReset]);

  return (
    <Card className={cn(isCircuitOpen && 'border-amber-500/30')}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500">{label}</p>
            <div className="mt-2 flex items-center gap-2">
              <StatusIndicator status={connection.status} />
              <span className={cn('text-sm font-semibold uppercase', statusColor)}>
                {statusText}
              </span>
            </div>
            {circuitText && (
              <div className="mt-1.5 flex items-center gap-2">
                <Zap className="h-3 w-3 text-amber-400" />
                <span className="text-[10px] font-medium text-amber-400">{circuitText}</span>
              </div>
            )}
            {connection.latencyMs !== undefined && connection.latencyMs > 0 && (
              <p className="mt-1 text-[10px] text-gray-500">{connection.latencyMs}ms latency</p>
            )}
            {connection.status === 'not-configured' && (
              <Link to={ROUTES.CONNECTIONS} className="mt-1 text-[10px] text-emerald-400 hover:underline">
                Configure Connection →
              </Link>
            )}
          </div>
        </div>
        {isCircuitOpen && resetTarget && (
          <button
            onClick={handleResetCircuit}
            disabled={isResetting}
            className="mt-3 flex w-full items-center justify-center gap-1.5 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider text-amber-400 transition-colors hover:bg-amber-500/20 disabled:opacity-50"
          >
            <RotateCcw className={cn('h-3 w-3', isResetting && 'animate-spin')} />
            {isResetting ? 'Resetting...' : 'Reset Circuit'}
          </button>
        )}
      </CardContent>
    </Card>
  );
}

function QueueHealthCard({ label, queue, direction }: { label: string; queue: QueueHealth; direction: 'downstream' | 'upstream' }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500">{label}</p>
            <div className="mt-2 flex items-center gap-2">
              <StatusIndicator status={queue.status} />
              <span className={cn(
                'text-sm font-semibold uppercase',
                queue.status === 'healthy' ? 'text-emerald-400' : queue.status === 'degraded' ? 'text-amber-400' : 'text-red-400'
              )}>
                {queue.status}
              </span>
            </div>
          </div>
          {direction === 'downstream' ? (
            <ArrowDownRight className="h-6 w-6 text-gray-600" />
          ) : (
            <ArrowUpRight className="h-6 w-6 text-gray-600" />
          )}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-bold text-white">{queue.pending}</p>
            <p className="text-[10px] text-gray-500">Queued</p>
          </div>
          <div>
            <p className={cn('text-lg font-bold', queue.dlqDepth > 0 ? 'text-red-400' : 'text-white')}>{queue.dlqDepth}</p>
            <p className="text-[10px] text-gray-500">DLQ</p>
          </div>
          <div>
            <p className="text-lg font-bold text-white">{queue.inFlight}</p>
            <p className="text-[10px] text-gray-500">In-Flight</p>
          </div>
        </div>
        <p className="mt-2 text-[9px] text-gray-600 text-center">All environments · infrastructure total</p>
      </CardContent>
    </Card>
  );
}

function MetricCard({ label, value, subtext, trend, icon: Icon }: {
  label: string;
  value: string | number;
  subtext?: string;
  trend?: 'up' | 'down' | 'neutral';
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500">{label}</p>
            <p className="mt-2 text-3xl font-bold text-white">{value}</p>
            {subtext && (
              <p className={cn(
                'mt-1 text-[10px]',
                trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-gray-500'
              )}>
                {subtext}
              </p>
            )}
          </div>
          {Icon && (
            <div className="flex h-9 w-9 items-center justify-center rounded border border-white/10 bg-white/5">
              <Icon className="h-4 w-4 text-emerald-400" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function EntityStatusRow({ entity }: { entity: EntityStatus }) {
  const statusColors = {
    healthy: 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30',
    degraded: 'text-amber-400 bg-amber-500/20 border-amber-500/30',
    critical: 'text-red-400 bg-red-500/20 border-red-500/30',
  };

  return (
    <Link
      to={ROUTES.DEAD_LETTER_QUEUES}
      className="grid grid-cols-5 gap-4 py-3 text-xs transition-colors hover:bg-white/[0.02]"
    >
      <span className="font-medium uppercase text-white">{entity.entityType}</span>
      <span className="text-emerald-400">{entity.direction}</span>
      <span className="text-gray-400">{entity.lastSuccess ? formatTimeAgo(entity.lastSuccess) : '—'}</span>
      <span className={entity.errors24h > 0 ? 'text-red-400' : 'text-gray-400'}>{entity.errors24h}</span>
      <span className={cn('inline-flex w-fit items-center rounded border px-2 py-0.5 text-[10px] font-medium uppercase', statusColors[entity.status])}>
        {entity.status}
      </span>
    </Link>
  );
}

function QuotaWidget() {
  const { data: quota, isLoading } = useQuery<QuotaSummary>({
    queryKey: ['quota', 'current'],
    queryFn: () => getCurrentQuota(),
    staleTime: 120_000,
    retry: false,
  });

  if (isLoading) return null;
  if (!quota || quota.monthlyTransactionLimit === null) return null;

  const pct = Math.min(quota.usagePercent ?? 0, 100);
  const isOver = quota.isOverLimit;

  const barBg = isOver
    ? 'bg-red-500'
    : (quota.usagePercent ?? 0) >= 95
    ? 'bg-amber-500'
    : (quota.usagePercent ?? 0) >= 80
    ? 'bg-yellow-500'
    : 'bg-emerald-500';

  const formatK = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(0)}K` : String(n);

  return (
    <Link to={ROUTES.QUOTA}>
      <Card className={cn(
        'border-l-4 transition-colors hover:bg-white/[0.02]',
        isOver ? 'border-l-red-500' : (quota.alertLevel === 95) ? 'border-l-amber-500' : (quota.alertLevel === 80) ? 'border-l-yellow-500' : 'border-l-emerald-500'
      )}>
        <CardContent className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-500">
              // Quota_{quota.billingPeriod}
            </p>
            <div className="flex items-center gap-2">
              {isOver && (
                <span className="rounded bg-red-500/20 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-400">
                  Over Limit
                </span>
              )}
              {!isOver && quota.alertLevel && (
                <span className={cn(
                  'rounded px-1.5 py-0.5 text-[10px] font-bold uppercase',
                  quota.alertLevel === 95 ? 'bg-amber-500/20 text-amber-400' : 'bg-yellow-500/20 text-yellow-400'
                )}>
                  {quota.alertLevel}%
                </span>
              )}
              <BarChart3 className="h-4 w-4 text-gray-500" />
            </div>
          </div>
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="font-bold text-white">
              {formatK(quota.totalTransactions)} / {formatK(quota.monthlyTransactionLimit!)}
            </span>
            <span className={cn(
              'font-semibold',
              isOver ? 'text-red-400' : quota.alertLevel ? 'text-amber-400' : 'text-emerald-400'
            )}>
              {quota.usagePercent}%
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
            <div className={cn('h-full rounded-full', barBg)} style={{ width: `${pct}%` }} />
          </div>
          {quota.projectedOverageChargeUsd > 0 && (
            <p className="mt-2 text-[10px] text-amber-400">
              Projected overage: ${quota.projectedOverageChargeUsd.toFixed(2)}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function PollingStatusWidget() {
  const { data: polling, isLoading } = useQuery<PollingStatusResponse>({
    queryKey: ['polling-status'],
    queryFn: () => getPollingStatus(),
    staleTime: 30_000,
    refetchInterval: 30_000,
    retry: false,
  });

  if (isLoading || !polling) return null;
  if (!polling.pollingEnabled) return null;

  const latestUpstream = polling.upstream.entities
    .filter((e) => e.updatedAt)
    .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
    [0];
  const latestDownstream = polling.downstream.entities
    .filter((e) => e.updatedAt)
    .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
    [0];

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <Radio className="h-4 w-4 text-emerald-400" />
          <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-500">
            // Polling_Status
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex items-center justify-between rounded border border-white/10 bg-white/5 px-4 py-3">
            <div className="flex items-center gap-2">
              <ArrowUpFromLine className="h-3.5 w-3.5 text-blue-400" />
              <div>
                <span className="text-xs font-medium text-white">Upstream</span>
                <p className="text-[10px] text-gray-500">CAT2 &rarr; Epicor</p>
              </div>
            </div>
            <div className="text-right">
              {!polling.upstream.active ? (
                <span className="text-[10px] font-medium text-gray-500">Inactive</span>
              ) : polling.upstream.entities.length > 0 && polling.upstream.entities.every((e) => e.status === 'paused') ? (
                <span className="flex items-center gap-1 text-[10px] font-medium text-amber-400">
                  <Pause className="h-2.5 w-2.5" />
                  Paused
                </span>
              ) : (
                <>
                  <span className="flex items-center gap-1 text-[10px] font-medium text-emerald-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Active
                  </span>
                  <p className="text-[10px] text-gray-500">
                    {latestUpstream?.updatedAt ? formatTimeAgo(latestUpstream.updatedAt) : 'No data'}
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between rounded border border-white/10 bg-white/5 px-4 py-3">
            <div className="flex items-center gap-2">
              <ArrowDownToLine className="h-3.5 w-3.5 text-amber-400" />
              <div>
                <span className="text-xs font-medium text-white">Downstream</span>
                <p className="text-[10px] text-gray-500">Epicor &rarr; CAT2</p>
              </div>
            </div>
            <div className="text-right">
              {!polling.downstream.active ? (
                <span className="text-[10px] font-medium text-gray-500">Inactive</span>
              ) : polling.downstream.entities.length > 0 && polling.downstream.entities.every((e) => e.status === 'paused') ? (
                <span className="flex items-center gap-1 text-[10px] font-medium text-amber-400">
                  <Pause className="h-2.5 w-2.5" />
                  Paused
                </span>
              ) : (
                <>
                  <span className="flex items-center gap-1 text-[10px] font-medium text-emerald-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Active
                  </span>
                  <p className="text-[10px] text-gray-500">
                    {latestDownstream?.updatedAt ? formatTimeAgo(latestDownstream.updatedAt) : 'No data'}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
        {(polling.upstream.entities.length > 0 || polling.downstream.entities.length > 0) && (
          <p className="mt-2 text-[9px] text-gray-600 text-center">
            {polling.upstream.entities.length + polling.downstream.entities.length} entity watermarks tracked · 30s refresh
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function formatTimeAgo(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// ── Main Dashboard ──

export function DashboardPage() {
  const { data: dashboard, isLoading, refetch, isFetching } = useDashboardData();
  const { data: dlqStats } = useDlqStats();

  // Paused entities get a banner of their own. A pause is easy to forget, and
  // a forgotten "ignore" drops data every day it stays on, so it has to be
  // visible on the first screen rather than only on the registry page.
  const { data: pausesData } = useQuery({
    queryKey: ['entity-pauses'],
    queryFn: listEntityPauses,
    refetchInterval: 30_000,
  });
  const pauses = pausesData?.pauses ?? [];
  const ignoring = pauses.filter((p) => p.mode === 'ignore');

  // DynamoDB attention count — used for rich breakdown (by entity, by error)
  const totalDlq = dashboard?.attention.totalDlq ??
    ((dlqStats?.downstream?.depth ?? 0) + (dlqStats?.upstream?.depth ?? 0) + (dlqStats?.conflict?.depth ?? 0));

  // Live SQS depth — source of truth for banner and warnings (never stale)
  const sqsDlqTotal =
    (dashboard?.health.downstream?.dlqDepth ?? dlqStats?.downstream?.depth ?? 0) +
    (dashboard?.health.upstream?.dlqDepth ?? (dlqStats?.upstream?.depth ?? 0) + (dlqStats?.conflict?.depth ?? 0));

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-emerald-500">// Operations_Dashboard</p>
          <h1 className="text-2xl font-bold uppercase tracking-wide text-white">System Status</h1>
          <p className="mt-1 text-xs text-gray-500">Real-time integration health and performance</p>
        </div>
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Overall Status Banner */}
      {dashboard && dashboard.health.overallStatus !== 'healthy' && (
        <Card className={cn(
          'border-l-4',
          dashboard.health.overallStatus === 'down' ? 'border-l-red-500 bg-red-500/10' : 'border-l-amber-500 bg-amber-500/10'
        )}>
          <CardContent className="flex items-center gap-3 p-4">
            <AlertTriangle className={dashboard.health.overallStatus === 'down' ? 'h-5 w-5 text-red-400' : 'h-5 w-5 text-amber-400'} />
            <div>
              <p className="text-sm font-medium text-white">
                {dashboard.health.overallStatus === 'down' ? 'System Issues Detected' : 'System Degraded'}
              </p>
              <p className="text-xs text-gray-400">
                {Object.values(dashboard.health.connections ?? {})
                  .filter((c) => c.status !== 'connected')
                  .map((c) => `${c.name} connection issue.`)
                  .join(' ')}
                {sqsDlqTotal > 0 && ` ${sqsDlqTotal} messages in DLQ.`}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Paused Entities Banner */}
      {pauses.length > 0 && (
        <Card
          className={cn(
            'border-l-4',
            ignoring.length > 0 ? 'border-l-red-500 bg-red-500/10' : 'border-l-violet-500 bg-violet-500/10',
          )}
        >
          <CardContent className="flex items-center gap-3 p-4">
            <Pause className={ignoring.length > 0 ? 'h-5 w-5 text-red-400' : 'h-5 w-5 text-violet-400'} />
            <div className="flex-1">
              <p className="text-sm font-medium text-white">
                {pauses.length === 1 ? '1 entity paused' : `${pauses.length} entities paused`}
              </p>
              <p className="text-xs text-gray-400">
                {pauses
                  .map((p) => `${p.entityType} (${p.mode === 'hold' ? 'holding in DLQ' : 'ignoring'})`)
                  .join(', ')}
                .
                {ignoring.length > 0 && ' Ignored messages are not recoverable.'}
              </p>
            </div>
            <Link
              to={ROUTES.ENTITY_REGISTRY}
              className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-white"
            >
              Manage <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </CardContent>
        </Card>
      )}

      {/* System Health Row */}
      <div>
        <p className="mb-3 text-[10px] font-medium uppercase tracking-wider text-emerald-500">// System_Health</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Dynamic system connection cards */}
          {Object.values(dashboard?.health.connections ?? {}).map((conn) => (
            <ConnectionCard
              key={conn.systemId}
              label={`${conn.name}_API`}
              connection={conn}
              resetTarget={conn.systemId}
              onCircuitReset={() => refetch()}
            />
          ))}
          {/* Fallback if no connections configured */}
          {Object.keys(dashboard?.health.connections ?? {}).length === 0 && (
            <ConnectionCard
              label="No_Systems"
              connection={{ status: 'not-configured' }}
            />
          )}
          <QueueHealthCard
            label="Downstream"
            queue={dashboard?.health.downstream ?? { status: 'healthy', pending: 0, dlqDepth: dlqStats?.downstream?.depth ?? 0, inFlight: 0 }}
            direction="downstream"
          />
          <QueueHealthCard
            label="Upstream"
            queue={dashboard?.health.upstream ?? { status: 'healthy', pending: 0, dlqDepth: (dlqStats?.upstream?.depth ?? 0) + (dlqStats?.conflict?.depth ?? 0), inFlight: 0 }}
            direction="upstream"
          />
        </div>
      </div>

      {/* Throughput Metrics */}
      <div>
        <p className="mb-3 text-[10px] font-medium uppercase tracking-wider text-emerald-500">// Throughput_24H</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label="Messages_Succeeded"
            value={dashboard?.throughput.processed24h.toLocaleString() ?? '—'}
            subtext={dashboard?.throughput.byDirection ? `${dashboard.throughput.byDirection.downstream.processed} down / ${dashboard.throughput.byDirection.upstream.processed} up` : undefined}
            icon={Activity}
          />
          <MetricCard
            label="Success_Rate"
            value={`${dashboard?.throughput.successRate ?? 100}%`}
            subtext={(() => {
              if (!dashboard?.throughput.byDirection) return undefined;
              const succeeded = dashboard.throughput.byDirection.downstream.processed + dashboard.throughput.byDirection.upstream.processed;
              const failed = dashboard.throughput.byDirection.downstream.failed + dashboard.throughput.byDirection.upstream.failed;
              return `${succeeded.toLocaleString()} succeeded / ${failed.toLocaleString()} failed`;
            })()}
            trend={dashboard?.throughput.successRate && dashboard.throughput.successRate < 99 ? 'down' : 'up'}
            icon={CheckCircle2}
          />
          <MetricCard
            label="Avg_Latency"
            value={dashboard?.throughput.avgLatencyMs ? `${dashboard.throughput.avgLatencyMs}ms` : '—'}
            subtext={dashboard?.throughput.avgLatencyMs ? (dashboard.throughput.avgLatencyMs > 500 ? 'Above normal' : 'Normal') : 'No data'}
            trend={dashboard?.throughput.avgLatencyMs && dashboard.throughput.avgLatencyMs > 500 ? 'down' : 'neutral'}
            icon={Clock}
          />
          <MetricCard
            label="In_Flight"
            value={dashboard?.throughput.inFlight ?? 0}
            subtext="Currently processing"
            icon={Zap}
          />
        </div>
      </div>

      {/* Failed Messages Breakdown (shown when there are failures) */}
      {dashboard?.throughput.byDirection && (dashboard.throughput.byDirection.downstream.failed > 0 || dashboard.throughput.byDirection.upstream.failed > 0) && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4">
            <p className="mb-3 text-[10px] font-medium uppercase tracking-wider text-amber-400">// Failed_Messages_24H</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ArrowDownRight className="h-4 w-4 text-gray-500" />
                  <span className="text-xs uppercase text-gray-400">Downstream</span>
                </div>
                <span className={cn(
                  'text-lg font-bold',
                  dashboard.throughput.byDirection.downstream.failed > 0 ? 'text-red-400' : 'text-emerald-400'
                )}>
                  {dashboard.throughput.byDirection.downstream.failed}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ArrowUpRight className="h-4 w-4 text-gray-500" />
                  <span className="text-xs uppercase text-gray-400">Upstream</span>
                </div>
                <span className={cn(
                  'text-lg font-bold',
                  dashboard.throughput.byDirection.upstream.failed > 0 ? 'text-red-400' : 'text-emerald-400'
                )}>
                  {dashboard.throughput.byDirection.upstream.failed}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quota Widget */}
      <div className="mt-2">
        <QuotaWidget />
      </div>

      {/* Polling Status Widget */}
      <PollingStatusWidget />

      {/* Time Series Chart & Entity Metrics */}
      <div className="grid gap-6 lg:grid-cols-2">
        <TimeSeriesChart />
        <EntityMetricsTable />
      </div>

      {/* Attention Required */}
      {totalDlq > 0 && (
        <Card className="border-red-500/30 bg-red-500/5">
          <CardContent className="p-4">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wider text-red-400">// Attention_Required</p>
                <h3 className="mt-1 flex items-center gap-2 text-lg font-semibold text-white">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                  {totalDlq} DLQ Messages
                </h3>
              </div>
              <Link to={ROUTES.DEAD_LETTER_QUEUES}>
                <Button>
                  View DLQ
                  <ExternalLink className="ml-2 h-3.5 w-3.5" />
                </Button>
              </Link>
            </div>

            <div className="grid gap-6 sm:grid-cols-3">
              {/* By Entity */}
              <div>
                <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-gray-500">By Entity</p>
                <div className="space-y-1">
                  {(dashboard?.attention.byEntity ?? []).slice(0, 5).map(({ entityType, count }) => (
                    <div key={entityType} className="flex items-center justify-between text-xs">
                      <span className="uppercase text-gray-400">{entityType}</span>
                      <span className="font-semibold text-white">{count}</span>
                    </div>
                  ))}
                  {(!dashboard?.attention.byEntity || dashboard.attention.byEntity.length === 0) && (
                    <p className="text-xs text-gray-500">Loading...</p>
                  )}
                </div>
              </div>

              {/* By Error */}
              <div>
                <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-gray-500">By Error Type</p>
                <div className="space-y-1">
                  {(dashboard?.attention.byError ?? []).slice(0, 5).map(({ category, count }) => (
                    <div key={category} className="flex items-center justify-between text-xs">
                      <span className={cn(
                        'uppercase',
                        category === 'FATAL' ? 'text-red-400' : category === 'CIRCUIT_OPEN' ? 'text-cyan-400' : 'text-gray-400'
                      )}>
                        {category}
                      </span>
                      <span className="font-semibold text-white">{count}</span>
                    </div>
                  ))}
                  {(!dashboard?.attention.byError || dashboard.attention.byError.length === 0) && (
                    <p className="text-xs text-gray-500">Loading...</p>
                  )}
                </div>
              </div>

              {/* Oldest */}
              <div>
                <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-gray-500">Oldest Pending</p>
                <p className="text-lg font-bold text-white">
                  {dashboard?.attention.oldestPending ? formatTimeAgo(dashboard.attention.oldestPending) : '—'}
                </p>
                <p className="text-[10px] text-gray-500">Time since arrival</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Clear Banner — uses live SQS depth, not DynamoDB count which can lag */}
      {sqsDlqTotal === 0 && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="flex items-center gap-3 p-4">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            <div>
              <p className="text-sm font-medium text-white">All Systems Operational</p>
              <p className="text-xs text-gray-400">No messages in dead letter queues. Integration is running smoothly.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entity Status Table */}
      {dashboard?.entityStatus && dashboard.entityStatus.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-500">// Entity_Status</p>
                <h3 className="mt-1 text-sm font-semibold uppercase tracking-wider text-white">Entities with Errors</h3>
              </div>
              <Link
                to={ROUTES.DEAD_LETTER_QUEUES}
                className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-emerald-400 transition-colors hover:text-emerald-300"
              >
                View All <ChevronRight className="h-3 w-3" />
              </Link>
            </div>

            {/* Table Header */}
            <div className="grid grid-cols-5 gap-4 border-b border-white/10 pb-2 text-[10px] font-medium uppercase tracking-wider text-gray-500">
              <span>Entity</span>
              <span>Direction</span>
              <span>Last_Success</span>
              <span>Errors_24H</span>
              <span>Status</span>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-white/5">
              {dashboard.entityStatus.map((entity) => (
                <EntityStatusRow key={entity.entityType} entity={entity} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
}
